export type RetryWithBackoffOptions = {
	attempts?: number;
	baseDelayMs?: number;
	isRetryable?: (err: unknown) => boolean;
};

const DEFAULT_RETRYABLE = /5\d\d|429|network|fetch|timeout/i;
const NOT_RETRYABLE = /404|not found/i;

const defaultIsRetryable = (err: unknown): boolean => {
	if (err instanceof ProgressEvent) return true;
	if (err instanceof Error) {
		if (NOT_RETRYABLE.test(err.message)) return false;
		if (DEFAULT_RETRYABLE.test(err.message)) return true;
	}
	return false;
};

export const retryWithBackoff = async <T>(
	fn: () => Promise<T>,
	options: RetryWithBackoffOptions = {}
): Promise<T> => {
	const { attempts = 3, baseDelayMs = 500, isRetryable = defaultIsRetryable } = options;

	let lastError: unknown;
	for (let attempt = 0; attempt < attempts; attempt++) {
		try {
			return await fn();
		} catch (err) {
			lastError = err;
			if (attempt === attempts - 1) break;
			if (!isRetryable(err)) break;
			const delay = baseDelayMs * Math.pow(3, attempt);
			await new Promise<void>((resolve) => setTimeout(resolve, delay));
		}
	}
	throw lastError;
};
